<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useWizardForms } from '@/composables/useWizardForms'
import type { WizardForm } from '@/types/wizard-forms'
import WizardFormListItem from '@/components/organizer/wizard-forms/WizardFormListItem.vue'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import HeaderCreateButton from '@/components/shared/HeaderCreateButton.vue'

const { t } = useI18n()
const { forms, loading, error, fetchForms, deleteForm } = useWizardForms()

const confirmDeleteId = ref<number | null>(null)
const confirmDeleteName = ref('')
const deleteLoading = ref(false)
const successMessage = ref('')

onMounted(() => {
  void fetchForms()
})

function requestDelete(form: WizardForm): void {
  confirmDeleteId.value = form.id
  confirmDeleteName.value = form.name
  successMessage.value = ''
}

function cancelDelete(): void {
  confirmDeleteId.value = null
  confirmDeleteName.value = ''
}

async function confirmDelete(): Promise<void> {
  if (confirmDeleteId.value === null) return
  deleteLoading.value = true
  try {
    await deleteForm(confirmDeleteId.value)
    successMessage.value = t('wizard_forms.list.delete_success')
    confirmDeleteId.value = null
    confirmDeleteName.value = ''
  } finally {
    deleteLoading.value = false
  }
}
</script>

<template>
  <OrganizerLayout>

    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-3xl font-bold text-white">{{ t('wizard_forms.list.heading') }}</h1>
          <p class="text-slate-400 mt-1">{{ t('wizard_forms.list.subheading') }}</p>
        </div>
        <HeaderCreateButton to="/organizer/wizard-forms/create" :label="t('wizard_forms.list.create_button')" />
      </div>

      <div
        v-if="successMessage"
        class="mb-6 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-400"
      >
        {{ successMessage }}
      </div>

      <div
        v-if="error"
        class="mb-6 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
      >
        {{ t(error) }}
      </div>

      <AppSpinner v-if="loading" />

      <div
        v-else-if="!loading && forms.length === 0 && !error"
        class="rounded-2xl border border-dashed border-slate-700 bg-slate-800/30 px-8 py-16 text-center"
      >
        <div class="mx-auto mb-4 w-14 h-14 rounded-full bg-slate-800 flex items-center justify-center">
          <svg class="w-7 h-7 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p class="text-slate-400 text-sm mb-6">{{ t('wizard_forms.list.empty_state') }}</p>
        <RouterLink
          to="/organizer/wizard-forms/create"
          class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
        >
          {{ t('wizard_forms.list.create_button') }}
        </RouterLink>
      </div>

      <div v-else-if="forms.length > 0" class="space-y-3">
        <WizardFormListItem
          v-for="wf in forms"
          :key="wf.id"
          :form="wf"
          @delete="requestDelete(wf)"
        />
      </div>
    </main>

    <!-- Delete confirmation dialog -->
    <div
      v-if="confirmDeleteId !== null"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      @click.self="cancelDelete"
    >
      <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <h2 class="text-lg font-bold text-white mb-2">{{ t('wizard_forms.list.confirm_delete_title') }}</h2>
        <p class="text-sm text-slate-400 mb-6">
          {{ t('wizard_forms.list.confirm_delete_body', { name: confirmDeleteName }) }}
        </p>
        <div class="flex items-center gap-3 justify-end">
          <button
            type="button"
            class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
            @click="cancelDelete"
          >
            {{ t('wizard_forms.list.confirm_delete_cancel') }}
          </button>
          <button
            type="button"
            :disabled="deleteLoading"
            class="bg-red-500 hover:bg-red-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-2"
            @click="confirmDelete"
          >
            <svg v-if="deleteLoading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {{ deleteLoading ? t('common.loading') : t('wizard_forms.list.confirm_delete_confirm') }}
          </button>
        </div>
      </div>
    </div>
  </OrganizerLayout>
</template>
